import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Play, Star } from 'lucide-react';
import { formatTime, routeColors } from './routePlannerUtils';

function RoutePlannerRouteList({
  plan,
  loading,
  publishPlan,
  moveStop,
  moveStopToRoute,
  moveStopsToDestination,
  onResequenceRoute,
  moveDrafts,
  setMoveDrafts,
  availableDrivers = [],
}) {
  const [expandedRouteId, setExpandedRouteId] = useState(null);
  const [selectedStopIds, setSelectedStopIds] = useState([]);
  const [bulkDraft, setBulkDraft] = useState({ destination: '', sequence: '' });
  const [resequenceRouteId, setResequenceRouteId] = useState(null);

  const destinationGroups = useMemo(() => buildDestinationGroups(plan, availableDrivers), [plan, availableDrivers]);
  const defaultDestination = destinationGroups[0]?.options?.[0]?.value || '';

  if (!plan) {
    return <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">Sin plan cargado</div>;
  }

  function toggleStop(stopId) {
    setSelectedStopIds((current) =>
      current.includes(stopId) ? current.filter((id) => id !== stopId) : [...current, stopId]
    );
  }

  async function bulkMove() {
    const destination = bulkDraft.destination || defaultDestination;
    if (!selectedStopIds.length || !destination) return;
    await moveStopsToDestination(selectedStopIds, destination, bulkDraft.sequence);
    setSelectedStopIds([]);
    setBulkDraft({ destination: '', sequence: '' });
  }

  async function resequenceRoute(route) {
    if (!onResequenceRoute || (route.stops || []).length < 2) return;
    setResequenceRouteId(route.id);
    try {
      await onResequenceRoute(route);
    } finally {
      setResequenceRouteId(null);
    }
  }

  return (
    <div className="space-y-2">
      {plan.summary?.google_fallback_reason && (
        <div className="rounded-lg bg-amber-50 p-2 text-xs font-semibold text-amber-700">
          Google fallback: {plan.summary.google_fallback_reason}
        </div>
      )}
      <button
        onClick={publishPlan}
        disabled={loading || plan.status === 'published'}
        className="w-full rounded-lg bg-brand-green px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
      >
        Publicar rutas
      </button>

      {!!selectedStopIds.length && (
        <BulkMoveBar
          count={selectedStopIds.length}
          draft={bulkDraft}
          setDraft={setBulkDraft}
          destinationGroups={destinationGroups}
          defaultDestination={defaultDestination}
          onMove={bulkMove}
          onClear={() => setSelectedStopIds([])}
        />
      )}

      {(plan.routes || []).map((route, routeIndex) => (
        <DriverRouteItem
          key={route.id}
          route={route}
          routeIndex={routeIndex}
          expanded={expandedRouteId === route.id}
          onToggle={() => setExpandedRouteId((current) => (current === route.id ? null : route.id))}
          moveStop={moveStop}
          moveStopToRoute={moveStopToRoute}
          onResequenceRoute={resequenceRoute}
          resequencing={resequenceRouteId === route.id}
          moveDrafts={moveDrafts}
          setMoveDrafts={setMoveDrafts}
          destinationGroups={destinationGroups}
          selectedStopIds={selectedStopIds}
          toggleStop={toggleStop}
        />
      ))}
    </div>
  );
}

function buildDestinationGroups(plan, availableDrivers) {
  const routes = plan?.routes || [];
  const routeDriverIds = new Set(routes.map((route) => Number(route.driver_id)).filter(Boolean));
  const routeOptions = routes.map((route) => ({
    value: `route:${route.id}`,
    label: route.driver_name || `Driver ${route.driver_id}`,
  }));
  const driverOptions = availableDrivers
    .filter((driver) => driver.user_id && !routeDriverIds.has(Number(driver.user_id)))
    .map((driver) => ({
      value: `driver:${driver.user_id}`,
      label: `${driver.name || `Driver ${driver.user_id}`} (nuevo)`,
    }));

  return [
    { label: 'Rutas del plan', options: routeOptions },
    { label: 'Conductores disponibles', options: driverOptions },
  ].filter((group) => group.options.length);
}

function DestinationSelect({ value, onChange, destinationGroups }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="min-w-[120px] flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs"
    >
      {destinationGroups.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function BulkMoveBar({ count, draft, setDraft, destinationGroups, defaultDestination, onMove, onClear }) {
  return (
    <div className="rounded-lg border border-brand-primary bg-pink-50 p-2">
      <div className="mb-2 text-xs font-bold text-brand-primary">{count} seleccionadas</div>
      <div className="flex flex-wrap items-center gap-2">
        <DestinationSelect
          value={draft.destination || defaultDestination}
          onChange={(destination) => setDraft((current) => ({ ...current, destination }))}
          destinationGroups={destinationGroups}
        />
        <input
          type="number"
          min="1"
          value={draft.sequence}
          onChange={(event) => setDraft((current) => ({ ...current, sequence: event.target.value }))}
          className="w-14 rounded-lg border border-slate-200 px-2 py-1 text-xs"
          placeholder="#"
        />
        <button onClick={onMove} className="rounded-lg bg-slate-900 px-2 py-1 text-xs font-bold text-white">
          Mover seleccionadas
        </button>
        <button onClick={onClear} className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-bold text-slate-600">
          Limpiar
        </button>
      </div>
    </div>
  );
}

function DriverRouteItem({
  route,
  routeIndex,
  expanded,
  onToggle,
  moveStop,
  moveStopToRoute,
  onResequenceRoute,
  resequencing,
  moveDrafts,
  setMoveDrafts,
  destinationGroups,
  selectedStopIds,
  toggleStop,
}) {
  const color = routeColors[routeIndex % routeColors.length];
  const warningCount = (route.stops || []).filter((stop) => stop.warning).length;

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center justify-between gap-2">
      <button onClick={onToggle} className="min-w-0 flex-1 p-3 text-left">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
            <span className="truncate text-sm font-bold text-slate-900">{route.driver_name || `Driver ${route.driver_id}`}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {route.total_stops} paradas | fin {formatTime(route.estimated_end_time)}
            {warningCount ? ` | ${warningCount} tarde` : ''}
          </div>
        </div>
      </button>
        <button
          onClick={(event) => {
            event.stopPropagation();
            onResequenceRoute(route);
          }}
          disabled={resequencing || (route.stops || []).length < 2}
          className="mr-3 rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          title="Reordenar esta ruta"
        >
          <Play className={`h-4 w-4 shrink-0 ${resequencing ? 'animate-pulse' : ''}`} style={{ color }} />
        </button>
      </div>
      {expanded && (
        <div className="max-h-[380px] space-y-2 overflow-y-auto border-t border-slate-200 p-2">
          {(route.stops || []).map((stop) => (
            <RouteStopRow
              key={stop.id}
              stop={stop}
              route={route}
              color={color}
              moveStop={moveStop}
              moveStopToRoute={moveStopToRoute}
              moveDrafts={moveDrafts}
              setMoveDrafts={setMoveDrafts}
              destinationGroups={destinationGroups}
              selected={selectedStopIds.includes(stop.id)}
              onSelect={() => toggleStop(stop.id)}
            />
          ))}
          {!route.stops?.length && <div className="p-3 text-center text-sm text-slate-500">Sin paradas</div>}
        </div>
      )}
    </div>
  );
}

function RouteStopRow({
  stop,
  route,
  color,
  moveStop,
  moveStopToRoute,
  moveDrafts,
  setMoveDrafts,
  destinationGroups,
  selected,
  onSelect,
}) {
  const draft = moveDrafts[stop.id] || {};
  const currentDestination = draft.target_destination || `route:${route.id}`;

  return (
    <div
      className={`rounded-lg border bg-white p-2 ${
        stop.warning ? 'border-red-300 ring-1 ring-red-100' : selected ? 'border-brand-primary' : 'border-slate-200'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <label className="flex min-w-0 items-start gap-2">
          <input type="checkbox" checked={selected} onChange={onSelect} className="mt-1" />
          <span className="min-w-0">
            <span className="block truncate text-sm font-bold text-slate-900">
              <span style={{ color }}>#{stop.sequence}</span> {stop.delivery?.recipient_name}
            </span>
            <span className="block text-xs text-slate-500">
              {stop.delivery?.district || 'Sin distrito'} | slot {stop.delivery?.slot || 0} | ETA {formatTime(stop.planned_arrival_time)}
            </span>
          </span>
        </label>
        <span className="flex shrink-0 items-center gap-1">
          {stop.warning && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">Tarde</span>}
          {stop.is_priority && <Star className="h-4 w-4 text-amber-500" />}
        </span>
      </div>
      {stop.warning && <div className="mt-2 rounded-lg bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">{stop.warning}</div>}
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <button onClick={() => moveStop(stop, route.id, -1)} className="rounded-lg border border-slate-200 p-1 text-slate-600" title="Subir">
          <ChevronUp className="h-4 w-4" />
        </button>
        <button onClick={() => moveStop(stop, route.id, 1)} className="rounded-lg border border-slate-200 p-1 text-slate-600" title="Bajar">
          <ChevronDown className="h-4 w-4" />
        </button>
        <DestinationSelect
          value={currentDestination}
          onChange={(target_destination) => setMoveDrafts((current) => ({ ...current, [stop.id]: { ...current[stop.id], target_destination } }))}
          destinationGroups={destinationGroups}
        />
        <input
          type="number"
          min="1"
          value={draft.target_sequence || ''}
          onChange={(event) => setMoveDrafts((current) => ({ ...current, [stop.id]: { ...current[stop.id], target_sequence: event.target.value } }))}
          className="w-14 rounded-lg border border-slate-200 px-2 py-1 text-xs"
          placeholder="#"
        />
        <button onClick={() => moveStopToRoute(stop, currentDestination)} className="rounded-lg bg-slate-900 px-2 py-1 text-xs font-bold text-white">
          Mover
        </button>
      </div>
    </div>
  );
}

export default RoutePlannerRouteList;
